import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client'
import type {
  Agent, GithubConnection, Integration, MachineToken, Me, Member, MintedToken, Organisation,
  OrganisationMachine, RepositoryRoom,
} from '../api/types'
import {
  FACE_SLOT_COUNT, FALLBACK_FACE_STYLE, Face, faceArt, isFaceStyle, type FaceSetView,
} from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { roomName } from '../room/format'
import { Aside, Label, Toggle } from './controls'
import { FacePicker } from './FacePicker'
import { FaceStyleChooser } from './FaceStyleChooser'
import type { GithubLinkOutcome } from './githubLink'
import type { GoogleLinkOutcome } from './googleLink'
import { InstallPane } from './InstallPane'
import { SetupPane } from './Setup'
import { LessonsPane } from '../lessons/Lessons'
import { WaysIn } from './WaysIn'
import { InstallCommand, LOGIN_COMMAND } from '../install/InstallCommand'

// 'Install' comes first because nothing else in here does anything until a
// machine has the command, and settings opens on it while none has signed in.
// 'Connections' lists the clients authorized to act as you, which is what the
// guide means by `stagepost login`. Setup, two panes down,
// lists machines, so neither name has to carry the other's meaning.
// 'Lessons' and 'Setup' are also in the repository rail, which is where they
// belong: both are things you go and read rather than things you configure, and
// a corpus of what the organisation has learned was reachable from nowhere at
// all before. They stay here because compact has no rail, and its bottom bar
// has four slots that are already spoken for.
const PANES = ['Install', 'Account', 'Organisation', 'GitHub', 'Avatars', 'Connections',
               'Lessons', 'Setup', 'History', 'Danger zone'] as const
export type Pane = (typeof PANES)[number]

/** Retention options. Past three days the room stops being a live conversation. */
const RETENTION_CHOICES = [1, 3, 7, 14]

/**
 * A pane's link into the guide.
 *
 * A new tab rather than a navigation: settings is a modal over the workspace,
 * so following the link in place would close it and lose the pane someone was
 * halfway through. The fragment matters as much as the path, because landing at
 * the top of the guide and hunting for the right section is barely help at all.
 */
function GuideLink({ anchor, children }: { anchor: string; children: React.ReactNode }) {
  return (
    <a
      href={`/guide#${anchor}`}
      target="_blank"
      rel="noreferrer"
      style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-bright)' }}
    >
      {children} ↗
    </a>
  )
}

/** A small, quiet button. Three of these sit under the face. */
function Chip({ children, onClick, on = false }: {
  children: React.ReactNode
  onClick: () => void
  on?: boolean
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      style={{
        padding: '8px 14px', borderRadius: radius.sm, cursor: 'pointer',
        fontSize: 12.5, fontWeight: 700,
        background: on ? 'var(--accent-wash)' : 'var(--panel-strong)',
        color: on ? 'var(--accent-bright)' : 'var(--text-dim)',
        border: `1px solid ${on ? 'var(--accent-edge)' : 'var(--border-strong)'}`,
      }}
    >
      {children}
    </button>
  )
}

/**
 * One room, and whether it is listening.
 *
 * There is no name field. Which repositories are connected is decided on
 * GitHub's installation screen, and asking again here would be asking twice.
 */
function RoomRow({ room, roomLabel, onMute }: {
  room: RepositoryRoom
  roomLabel: string
  onMute: (enabled: boolean) => Promise<void>
}) {
  const [failure, setFailure] = useState<string | null>(null)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: '400 12px var(--font-machine)', color: 'var(--text-faint)', marginBottom: 3 }}>
          {roomLabel}
        </div>
        <div style={{ font: '400 13px var(--font-machine)', color: room.linked ? 'var(--text)' : 'var(--text-dim)' }}>
          {room.github_full_name ?? 'Not connected'}
        </div>
        {failure && (
          <div role="alert" style={{ fontSize: 11.5, color: 'var(--deletions)', marginTop: 5 }}>
            {failure}
          </div>
        )}
      </div>
      {room.linked && (
        <Toggle
          label={roomLabel}
          on={room.enabled}
          onChange={async (next) => {
            setFailure(null)
            try {
              await onMute(next)
            } catch (error) {
              setFailure(error instanceof Error ? error.message : 'not saved')
            }
          }}
        />
      )}
    </div>
  )
}

/**
 * Something that cannot be taken back, and the exact word that unlocks it.
 *
 * The confirmation is typing the thing's own name rather than clicking twice.
 * A second button is a second click, which is what a person on autopilot does
 * anyway; typing `enumlabs` is not something anyone does by accident.
 */
function Irreversible({
  title, description, confirmWord, confirmLabel, action, actionLabel, onDone,
}: {
  title: string
  description: React.ReactNode
  confirmWord: string
  confirmLabel: string
  action: () => Promise<void>
  actionLabel: string
  onDone?: () => void
}) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const armed = typed.trim() === confirmWord && !busy

  return (
    <section style={{ padding: '16px 18px', marginBottom: 18, borderRadius: radius.md, background: 'rgba(229,98,74,.07)', border: '1px solid rgba(229,98,74,.25)' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 13.5, fontWeight: 800, color: 'var(--deletions)' }}>
        {title}
      </h3>
      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-dim)', marginBottom: 14 }}>
        {description}
      </div>
      <input
        value={typed}
        aria-label={confirmLabel}
        placeholder={confirmWord}
        onChange={(event) => setTyped(event.target.value)}
        style={{ width: '100%', maxWidth: 320, fontSize: 13, color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', borderRadius: radius.sm, padding: '9px 12px', marginBottom: 12 }}
      />
      {failure && (
        <div role="alert" style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--deletions)', marginBottom: 12 }}>
          {failure}
        </div>
      )}
      <div>
        <button
          disabled={!armed}
          onClick={async () => {
            setBusy(true)
            setFailure(null)
            try {
              await action()
              onDone?.()
            } catch (error) {
              setFailure(error instanceof Error ? error.message : 'not deleted')
            } finally {
              setBusy(false)
            }
          }}
          style={{
            padding: '10px 16px', borderRadius: radius.sm,
            cursor: armed ? 'pointer' : 'not-allowed', opacity: armed ? 1 : 0.4,
            background: 'var(--deletions)', color: 'var(--ground)',
            fontSize: 12.5, fontWeight: 700,
          }}
        >
          {busy ? 'Deleting…' : actionLabel}
        </button>
      </div>
    </section>
  )
}

/**
 * One agent's face, and the drop target that changes it.
 *
 * A face is unique per organisation, so the server can refuse a claim that
 * another agent already holds. That refusal is shown here rather than
 * swallowed: two agents wearing the same face is exactly the confusion the
 * constraint exists to prevent, and silently doing nothing looks like a bug.
 */
function FaceSlot({
  agent, faceStyle, faceSet, onClaim,
}: {
  agent: Agent
  /**
   * The raw string, not `FaceStyle`: an organisation on its own pool carries
   * `set:<slug>`, which faceArt resolves against `faceSet` below rather than
   * against one of the five built-ins.
   */
  faceStyle: string
  faceSet: FaceSetView | null
  onClaim: (file: File) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [over, setOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  async function claim(file: File | undefined) {
    if (!file) return
    setBusy(true)
    setFailure(null)
    try {
      await onClaim(file)
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'not saved')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 84 }}>
      <button
        onClick={() => fileRef.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setOver(false)
          void claim(event.dataTransfer.files[0])
        }}
        aria-label={`Give ${agent.name} an avatar`}
        style={{
          width: 84, height: 84, cursor: 'pointer', padding: 0, position: 'relative',
          borderRadius: 26, overflow: 'hidden',
          background: 'var(--panel-strong)',
          border: `1px ${over ? 'solid' : 'dashed'} ${over ? 'var(--accent)' : 'var(--border-strong)'}`,
          opacity: busy ? 0.5 : 1,
        }}
      >
        {/* Through the resolver, not a hard-coded /media path: this key is a
            pool slot as often as it is an upload. */}
        {(() => {
          const art = faceArt(agent.name, agent.avatar_key, faceStyle, { online: agent.online }, faceSet)
          return art.kind === 'image'
            ? <img src={art.src} alt={agent.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (
              <span style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', padding: 6 }}>
                <Face name={agent.name} size={56} online={agent.online} avatarKey={agent.avatar_key} />
              </span>
            )
        })()}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(event) => void claim(event.target.files?.[0])}
        style={{ display: 'none' }}
      />
      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-dim)' }}>{agent.name}</span>
      {failure && (
        <span role="alert" style={{ fontSize: 10.5, lineHeight: 1.4, color: 'var(--deletions)', textAlign: 'center' }}>
          {failure}
        </span>
      )}
    </div>
  )
}

export function Settings({
  me, organisation, machines, compact = false, githubLink = null, googleLink = null,
  initialPane, onMeChanged,
  onOrganisationChanged, onLinksChanged, onOrganisationDeleted, onAccountDeleted,
  onSignedOut, onClose,
}: {
  me: Me
  organisation: Organisation
  /** For Setup's runner lines, live from the organisation stream. */
  machines: OrganisationMachine[] | null
  compact?: boolean
  /**
   * How a GitHub link attempt came back, when this was opened by returning
   * from one. Read out of the address bar by whoever opened this pane, because
   * a component that reads the URL for itself cannot be rendered twice.
   */
  githubLink?: GithubLinkOutcome | null
  /** Same idea, for a Google link attempt. */
  googleLink?: GoogleLinkOutcome | null
  /**
   * Where to open. Left out, settings opens on Install while this account has
   * no machine signed in and on Account otherwise, except that a link outcome
   * always opens Account, which is where its message is shown.
   */
  initialPane?: Pane
  onMeChanged?: (me: Me) => void
  /**
   * The rail tile and the room-list header both read the organisation, and the
   * name is not the only thing a rename moves — the tile's two letters are
   * derived from it — so this hands back the whole row the server stored.
   */
  onOrganisationChanged?: (organisation: Organisation) => void
  /** A link changes what a room is attached to, which the room header shows. */
  onLinksChanged?: () => void
  /**
   * The organisation on screen no longer exists. The rail is pointing at
   * nothing, so the app has to be told rather than left to notice.
   */
  onOrganisationDeleted?: () => void
  /** There is no account any more. Everything below this is gone with it. */
  onAccountDeleted?: () => void
  /**
   * This browser is no longer signed in. The account and every agent are
   * untouched — only the cookie is gone.
   */
  onSignedOut?: () => void
  onClose: () => void
}) {
  const [pane, setPane] = useState<Pane>(initialPane ?? 'Account')
  const [displayName, setDisplayName] = useState(me.display_name)
  const [notify, setNotify] = useState(me.notify)
  const [members, setMembers] = useState<Member[]>([])
  const [connection, setConnection] = useState<GithubConnection | null>(null)
  const [confirmingDisconnect, setConfirmingDisconnect] = useState<number | null>(null)
  const [agents, setAgents] = useState<Agent[]>([])
  const [tokens, setTokens] = useState<MachineToken[]>([])
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [minted, setMinted] = useState<MintedToken | null>(null)
  const [retention, setRetention] = useState(organisation.retention_days)
  const [invitee, setInvitee] = useState('')
  const [organisationName, setOrganisationName] = useState(organisation.name)
  const [isDefault, setIsDefault] = useState(
    me.default_organisation === organisation.slug)
  const [renameFailure, setRenameFailure] = useState<string | null>(null)
  const [memberActionFailure, setMemberActionFailure] = useState<string | null>(null)
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null)
  // The raw string, not narrowed through isFaceStyle: an organisation on its
  // own pool carries `set:<slug>`, which isFaceStyle would discard, putting
  // the pane on the `patterns` floor while the room beside it draws the real
  // pool. Same reasoning as Workspace.tsx, which this mirrors.
  const [faceStyle, setFaceStyle] = useState<string>(organisation.face_style)
  const [picking, setPicking] = useState(false)
  const [githubConfigured, setGithubConfigured] = useState(false)
  const [googleConfigured, setGoogleConfigured] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [signOutFailure, setSignOutFailure] = useState<string | null>(null)
  const portraitInput = useRef<HTMLInputElement>(null)

  /**
   * Which slots agents in this organisation already wear.
   *
   * The Account pane dims those rather than reserving them: a human is not in
   * the pool's bookkeeping, so this is what the room looks like now, not a
   * promise about tomorrow.
   */
  const wornByAgents = useMemo(
    () => Object.fromEntries(
      agents.filter((agent) => agent.avatar_key)
        .map((agent) => [agent.avatar_key as string, agent.name])),
    [agents],
  )

  /**
   * The organisation's own pool, expanded, the way faceArt needs it. Mirrors
   * Workspace.tsx exactly, so a face drawn in this pane and a face drawn in
   * the room beside it are the same resolution reaching the same answer.
   */
  const faceSet: FaceSetView | null = useMemo(() => {
    const set = organisation.face_set
    if (!set) return null
    return {
      slug: set.slug,
      // The server enforces that a fallback names a built-in
      // (`_check_fallback_style`), but the wire type is a plain string, so
      // this is the one place that fact is worth checking rather than trusted.
      fallbackStyle: isFaceStyle(set.fallback_style) ? set.fallback_style : FALLBACK_FACE_STYLE,
      slots: Object.fromEntries(set.slots.map((slot) => [slot.slot, { url: slot.url, name: slot.name }])),
    }
  }, [organisation.face_set])

  /**
   * Renaming and retention are the owner's, and the server refuses them from
   * anyone else. Rendering the controls anyway would mean a field that accepts
   * a name it cannot keep and a button that moves before the request fails.
   */
  const owns = organisation.role === 'owner'

  // Fetched on opening rather than with a pane, because it decides which pane
  // that is. Only a pane nobody chose moves: a click that landed while this was
  // in flight wins.
  const choseOpeningPane = initialPane !== undefined || githubLink !== null || googleLink !== null
  useEffect(() => {
    void api.tokens().then((rows) => {
      setTokens(rows)
      if (!rows.length && !choseOpeningPane) {
        setPane((current) => (current === 'Account' ? 'Install' : current))
      }
    })
  }, [choseOpeningPane])

  useEffect(() => {
    // The Account pane needs the roster too, to say which faces are spoken
    // for, and the sign-in methods to know whether there is a GitHub App or a
    // Google client to link to at all.
    if (pane === 'Account') {
      void api.organisationAgents(organisation.slug).then(setAgents)
      void api.signInMethods().then((methods) => {
        setGithubConfigured(methods.github)
        setGoogleConfigured(methods.google)
      })
    }
    if (pane === 'Organisation') void api.members(organisation.slug).then(setMembers)
    if (pane === 'Organisation' && owns) void api.integrations(organisation.slug).then(setIntegrations)
    if (pane === 'GitHub') void api.githubConnection(organisation.slug).then(setConnection)
    if (pane === 'Avatars') void api.organisationAgents(organisation.slug).then(setAgents)
    if (pane === 'Connections') void api.tokens().then(setTokens)
  }, [pane, organisation.slug, owns])

  return (
    <div
      role="dialog"
      aria-label="Settings"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,.7)',
        backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center',
        // Compact gives the sheet the whole screen; there is no "outside the
        // dialog" worth showing on a phone, and a 20px frame around a settings
        // pane is 40px of scrolling nobody asked for.
        padding: compact ? 0 : 20,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: compact ? '100%' : 'min(920px,100%)',
          height: compact ? '100%' : 'min(660px,100%)',
          background: 'var(--ground-raised)',
          border: compact ? 'none' : '1px solid var(--border-strong)',
          borderRadius: compact ? 0 : radius.xl,
          display: 'flex', flexDirection: compact ? 'column' : 'row', overflow: 'hidden',
          boxShadow: compact ? 'none' : 'var(--shadow-overlay)',
          paddingTop: compact ? 'var(--safe-top)' : 0,
          paddingLeft: compact ? 'var(--safe-left)' : 0,
          paddingRight: compact ? 'var(--safe-right)' : 0,
        }}
      >
        {/* Compact turns the sidebar into a scrolling row of tabs. Eight sections
            in a column would eat half a phone screen before any content. */}
        <nav
          aria-label="Settings sections"
          // `sp-scroller` only when it is a row: it carries momentum, snapping
          // and a hidden scrollbar, all of which are wrong for a column that
          // fits.
          className={compact ? 'sp-scroller' : undefined}
          style={{
            width: compact ? '100%' : 196, flex: 'none', display: 'flex',
            flexDirection: compact ? 'row' : 'column', gap: compact ? 4 : 2,
            padding: compact ? '12px 12px' : '20px 12px',
            borderRight: compact ? 'none' : '1px solid var(--border)',
            borderBottom: compact ? '1px solid var(--border)' : 'none',
          }}
        >
          {PANES.map((name) => (
            <button
              key={name}
              onClick={() => setPane(name)}
              aria-current={pane === name}
              style={{
                // Eight of these scroll sideways on a phone, so each one is a
                // target in a row and takes its height rather than borrowing a
                // hit area that would overlap the tab beside it.
                padding: compact ? '10px 13px' : '9px 11px',
                minHeight: compact ? 40 : undefined,
                flex: compact ? 'none' : undefined,
                borderRadius: radius.sm,
                cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
                fontSize: 13, fontWeight: pane === name ? 700 : 500,
                color: pane === name ? 'var(--text)' : 'var(--text-dim)',
                background: pane === name ? 'var(--panel-strong)' : 'transparent',
              }}
            >
              {name}
            </button>
          ))}
        </nav>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 22px 14px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
            <h2 style={{ flex: 1, margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: '-.02em' }}>
              {pane}
            </h2>
            <button onClick={onClose} aria-label="Close settings" className="sp-tap" style={{ width: 28, height: 28, borderRadius: radius.xs, background: 'var(--panel-strong)', cursor: 'pointer', flex: 'none' }}>✕</button>
          </header>

          <div
            className="sp-settings-pane"
            style={{
              flex: 1, minHeight: 0, overflowY: 'auto',
              // The home indicator, plus room to scroll past the last control
              // rather than ending flush against it.
              padding: compact
                ? '18px 18px calc(26px + var(--safe-bottom))'
                : '20px 22px 26px',
              overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch',
            }}
          >
            {pane === 'Install' && (
              <InstallPane tokens={tokens} onShowConnections={() => setPane('Connections')} />
            )}

            {pane === 'Account' && (
              <>
                {/* At most one of the two is ever set — a page load reads at
                    most one link-outcome parameter out of the address bar. */}
                {(githubLink ?? googleLink) && (
                  <div
                    role="alert"
                    style={{
                      padding: '12px 14px', marginBottom: 18, borderRadius: radius.md,
                      fontSize: 12.5, lineHeight: 1.55,
                      background: (githubLink ?? googleLink)!.ok ? 'var(--accent-wash)' : 'rgba(229,98,74,.09)',
                      border: `1px solid ${(githubLink ?? googleLink)!.ok ? 'var(--accent-edge)' : 'rgba(229,98,74,.22)'}`,
                      color: (githubLink ?? googleLink)!.ok ? 'var(--accent-bright)' : 'var(--deletions)',
                    }}
                  >
                    {(githubLink ?? googleLink)!.message}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                  {/* Through the resolver, because this key is a pool slot as
                      often as it is an upload. A human's slot is not reserved
                      the way an agent's is — the picker says which ones agents
                      wear and the person decides. */}
                  <span style={{ flex: 'none' }}>
                    <Face
                      name={me.display_name}
                      size={56}
                      avatarKey={me.avatar_key}
                      ring="rgba(110,168,254,.6)"
                    />
                  </span>
                  <input
                    ref={portraitInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    onChange={async (event) => {
                      const file = event.target.files?.[0]
                      if (!file) return
                      const stored = await api.uploadMedia(file)
                      onMeChanged?.(await api.updateMe({ avatar_key: stored.key }))
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Label>Display name</Label>
                    <input
                      value={displayName}
                      aria-label="Display name"
                      onChange={(event) => setDisplayName(event.target.value)}
                      onBlur={async () => {
                        const trimmed = displayName.trim()
                        if (!trimmed || trimmed === me.display_name) return
                        onMeChanged?.(await api.updateMe({ display_name: trimmed }))
                      }}
                      style={{ width: '100%', maxWidth: 280, fontSize: 15, fontWeight: 700, color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', borderRadius: radius.sm, padding: '9px 12px' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 18 }}>
                  <Chip on={picking} onClick={() => setPicking(!picking)}>
                    Pick a face
                  </Chip>
                  <Chip onClick={() => portraitInput.current?.click()}>
                    Upload a portrait
                  </Chip>
                  {me.avatar_key && (
                    <Chip onClick={async () => {
                      setPicking(false)
                      onMeChanged?.(await api.updateMe({ avatar_key: null }))
                    }}>
                      Remove
                    </Chip>
                  )}
                </div>

                {picking && (
                  <FacePicker
                    style={faceStyle}
                    set={faceSet}
                    value={me.avatar_key}
                    wornBy={wornByAgents}
                    onPick={async (key) => {
                      onMeChanged?.(await api.updateMe({ avatar_key: key }))
                    }}
                  />
                )}

                <Label>How you sign in</Label>
                <WaysIn
                  me={me}
                  githubConfigured={githubConfigured}
                  googleConfigured={googleConfigured}
                  onMeChanged={onMeChanged}
                />

                <div style={{ height: 26 }} />
                <Label>What reaches you</Label>
                {([
                  ['humans', 'A human speaks', 'Another person posts in a room you are in. Rare, and almost always for you.'],
                  ['mentions', 'You are mentioned', `An agent or a human writes @${displayName.toLowerCase()} anywhere.`],
                  ['all', 'Everything else', 'Every agent message in every room. Off by default — with five agents this is a firehose, and it stops scaling entirely as the team grows.'],
                ] as const).map(([key, label, description]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 0', borderTop: '1px solid var(--border)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4 }}>{label}</div>
                      <p style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-dim)', margin: 0 }}>{description}</p>
                    </div>
                    <Toggle
                      label={label}
                      on={notify[key]}
                      onChange={async (next) => {
                        const updated = { ...notify, [key]: next }
                        setNotify(updated)
                        onMeChanged?.(await api.updateMe({ notify: updated }))
                      }}
                    />
                  </div>
                ))}

                <div style={{ height: 26 }} />
                <Label>This browser</Label>
                <button
                  onClick={async () => {
                    setSignOutFailure(null)
                    setSigningOut(true)
                    try {
                      await api.logout()
                      onSignedOut?.()
                    } catch (error) {
                      // Never fail silently here. Someone who believes they
                      // signed out of a shared machine and did not is worse
                      // off than someone who knows the button did not work.
                      setSignOutFailure(
                        error instanceof Error ? error.message : 'sign out failed')
                    } finally {
                      setSigningOut(false)
                    }
                  }}
                  disabled={signingOut}
                  style={{
                    fontSize: 13, fontWeight: 700, color: 'var(--text)',
                    background: 'var(--panel-strong)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: radius.sm, padding: '9px 18px',
                    cursor: signingOut ? 'default' : 'pointer',
                    opacity: signingOut ? 0.6 : 1,
                  }}>
                  {signingOut ? 'Signing out…' : 'Sign out'}
                </button>
                {signOutFailure && (
                  <p role="alert" style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--deletions)' }}>
                    {signOutFailure}
                  </p>
                )}
                <Aside>
                  Only this browser. Your agents hold their own machine tokens
                  and keep talking to each other — revoke those under Connections.
                </Aside>
              </>
            )}

            {pane === 'Organisation' && (
              <>
                <Label>Name</Label>
                <div style={{ marginBottom: 26 }}>
                  {owns ? (
                    <input
                      value={organisationName}
                      aria-label="Organisation name"
                      maxLength={120}
                      onChange={(event) => setOrganisationName(event.target.value)}
                      onBlur={async () => {
                        const trimmed = organisationName.trim()
                        if (!trimmed || trimmed === organisation.name) return
                        setRenameFailure(null)
                        try {
                          onOrganisationChanged?.(
                            await api.updateOrganisation(organisation.slug, { name: trimmed }))
                        } catch (error) {
                          setRenameFailure(error instanceof Error ? error.message : 'not saved')
                        }
                      }}
                      style={{ width: '100%', maxWidth: 360, fontSize: 18, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', borderRadius: radius.sm, padding: '10px 13px' }}
                    />
                  ) : (
                    <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em' }}>
                      {organisation.name}
                    </div>
                  )}
                  {renameFailure && (
                    <div role="alert" style={{ fontSize: 11.5, color: 'var(--deletions)', marginTop: 6 }}>
                      {renameFailure}
                    </div>
                  )}
                </div>

                {/* Yours, not the organisation's — which is why it is open to
                    every member while the name above it is not. It changes
                    where *your* next `stagepost init` lands and nothing anyone
                    else can see. */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 0', borderTop: '1px solid var(--border)', marginBottom: 26 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4 }}>
                      New rooms land here
                    </div>
                    <p style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-dim)', margin: 0 }}>
                      Only for a repository nothing else can place — no room yet,
                      and no connected GitHub account that covers it. A repo that
                      already has a room stays where it is.
                    </p>
                  </div>
                  <Toggle
                    label="New rooms land here"
                    on={isDefault}
                    onChange={async (next) => {
                      setIsDefault(next)
                      onMeChanged?.(await api.updateMe({
                        default_organisation: next ? organisation.slug : '',
                      }))
                    }}
                  />
                </div>

                <Label>Humans</Label>
                {members.map((member) => (
                  <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 0', borderTop: '1px solid var(--border)' }}>
                    <Face
                      name={member.display_name}
                      avatarKey={member.avatar_key}
                      ring="rgba(110,168,254,.6)"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700 }}>{member.display_name}</div>
                      <div style={{ font: '400 11px var(--font-machine)', color: 'var(--text-faint)' }}>
                        {member.pending
                          ? 'invited, has not signed in yet'
                          : `${member.agent_count} agents in this organisation`}
                      </div>
                    </div>
                    {owns && member.id !== me.id && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {member.pending && (
                          <>
                            <button
                              onClick={async () => {
                                setMemberActionFailure(null)
                                try {
                                  await api.reinviteMember(organisation.slug, member.id)
                                } catch (error) {
                                  setMemberActionFailure(error instanceof Error ? error.message : 'could not resend')
                                }
                              }}
                              style={{ padding: '5px 10px', borderRadius: radius.xs, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', font: '600 11px var(--font-interface)', cursor: 'pointer' }}
                            >
                              Resend
                            </button>
                            <button
                              onClick={async () => {
                                setMemberActionFailure(null)
                                try {
                                  const { link } = await api.memberLoginLink(organisation.slug, member.id)
                                  await navigator.clipboard?.writeText(link)
                                } catch (error) {
                                  setMemberActionFailure(error instanceof Error ? error.message : 'could not copy link')
                                }
                              }}
                              style={{ padding: '5px 10px', borderRadius: radius.xs, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', font: '600 11px var(--font-interface)', cursor: 'pointer' }}
                            >
                              Copy login link
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setConfirmingRemoval(member.id)}
                          style={{ padding: '5px 10px', borderRadius: radius.xs, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', font: '600 11px var(--font-interface)', cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)' }}>
                      {member.id === me.id ? `${member.role} · you` : member.role}
                    </span>
                  </div>
                ))}
                {memberActionFailure && (
                  <div role="alert" style={{ fontSize: 11.5, color: 'var(--deletions)', marginTop: 6 }}>
                    {memberActionFailure}
                  </div>
                )}
                {confirmingRemoval !== null && (() => {
                  const target = members.find((candidate) => candidate.id === confirmingRemoval)
                  if (!target) return null
                  return (
                    <div role="alertdialog" aria-label="Remove member" style={{ padding: '14px 16px', borderRadius: radius.md, background: 'var(--panel-strong)', marginBottom: 12 }}>
                      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 10 }}>
                        {target.display_name} will lose access to {organisation.name}{' '}
                        immediately, agents included. They can be invited back later.
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          onClick={async () => {
                            setMemberActionFailure(null)
                            try {
                              await api.removeMember(organisation.slug, target.id)
                              setMembers(members.filter((candidate) => candidate.id !== target.id))
                              setConfirmingRemoval(null)
                            } catch (error) {
                              setMemberActionFailure(error instanceof Error ? error.message : 'could not remove')
                            }
                          }}
                          style={{ padding: '9px 16px', borderRadius: radius.sm, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                          Remove anyway
                        </button>
                        <button
                          onClick={() => setConfirmingRemoval(null)}
                          style={{ padding: '9px 16px', borderRadius: radius.sm, background: 'var(--panel-hover)', color: 'var(--text-dim)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )
                })()}
                {owns && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
                    <input
                      value={invitee}
                      aria-label="Invite by email"
                      placeholder="name@company.com"
                      onChange={(event) => setInvitee(event.target.value)}
                      style={{ flex: 1, minWidth: 200, fontSize: 13.5, color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', borderRadius: radius.sm, padding: '10px 13px' }}
                    />
                    <button
                      onClick={async () => {
                        if (!invitee.trim()) return
                        setMemberActionFailure(null)
                        try {
                          setMembers([...members, await api.invite(organisation.slug, invitee.trim())])
                          setInvitee('')
                        } catch (error) {
                          setMemberActionFailure(error instanceof Error ? error.message : 'could not invite')
                        }
                      }}
                      style={{ padding: '10px 18px', borderRadius: radius.sm, background: 'var(--accent-wash)', border: '1px solid var(--accent-edge)', color: 'var(--accent-bright)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Invite
                    </button>
                  </div>
                )}
                <Aside>
                  Agents are not invited. They belong to whoever runs them, and
                  appear in a room the first time one of them speaks.
                </Aside>
                {owns && (
                  <>
                    <Label>Integrations</Label>
                    <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 12 }}>
                      Services an owner connected this organisation to. Each keeps
                      working if the owner who approved it leaves, until an owner
                      revokes it here.
                    </p>
                    {integrations.map((integration) => (
                      <div key={integration.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 0', borderTop: '1px solid var(--border)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ font: '500 13px var(--font-machine)' }}>{integration.client_name}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 3 }}>
                            {integration.scope.split(' ').join(', ')}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 3 }}>
                            {integration.created_by ? `approved by ${integration.created_by}, ` : ''}
                            {integration.last_used_at ? `last used ${new Date(integration.last_used_at).toLocaleString()}` : 'never used'}
                          </div>
                        </div>
                        <button
                          aria-label={`Revoke ${integration.client_name}`}
                          onClick={async () => {
                            await api.revokeIntegration(organisation.slug, integration.id)
                            setIntegrations(integrations.filter((row) => row.id !== integration.id))
                          }}
                          style={{ padding: '7px 13px', borderRadius: radius.sm, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Revoke
                        </button>
                      </div>
                    ))}
                    {!integrations.length && (
                      <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)', padding: '12px 0' }}>
                        Not connected to anything.
                      </p>
                    )}
                  </>
                )}
              </>
            )}

            {pane === 'GitHub' && connection && (
              <>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 20 }}>
                  Read-only access to the repositories you pick, so their merges and
                  pushes arrive in the matching rooms. <GuideLink anchor="github">How this works</GuideLink>
                </p>
                {connection.installations.map((installation) => (
                  <div key={installation.installation_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: radius.md, background: 'var(--panel-strong)', marginBottom: 12 }}>
                    {installation.account_avatar_url && (
                      <img src={installation.account_avatar_url} alt="" style={{ width: 26, height: 26, borderRadius: '50%', flex: 'none' }} />
                    )}
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: installation.suspended ? 'var(--text-faint)' : 'var(--online)', flex: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: '500 13px var(--font-machine)' }}>
                        {installation.account_login}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                        {installation.suspended
                          ? 'Suspended on GitHub · delivering nothing'
                          : installation.repository_selection === 'all'
                            ? 'All repositories · read-only'
                            : `${installation.repository_count} repositories · read-only`}
                      </div>
                    </div>
                    <a href={installation.manage_url} target="_blank" rel="noreferrer"
                       style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                      Manage on GitHub
                    </a>
                    {owns && (
                      <button
                        onClick={() => setConfirmingDisconnect(installation.installation_id)}
                        style={{ padding: '7px 13px', borderRadius: radius.sm, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Disconnect
                      </button>
                    )}
                  </div>
                ))}

                {/* Stagepost cannot uninstall itself from GitHub — that needs a
                    key this server deliberately does not hold. Saying so is the
                    difference between an honest control and one that lies. */}
                {confirmingDisconnect !== null && (
                  <div role="alertdialog" aria-label="Disconnect GitHub" style={{ padding: '14px 16px', borderRadius: radius.md, background: 'var(--panel-strong)', marginBottom: 12 }}>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 10 }}>
                      Stagepost will forget this connection and stop posting its
                      events. The app is{' '}
                      <strong style={{ color: 'var(--text)' }}>still installed on GitHub</strong>
                      {' '}until you remove it there.
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        onClick={async () => {
                          await api.disconnectInstallation(organisation.slug, confirmingDisconnect)
                          setConfirmingDisconnect(null)
                          setConnection(await api.githubConnection(organisation.slug))
                          onLinksChanged?.()
                        }}
                        style={{ padding: '9px 16px', borderRadius: radius.sm, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                        Disconnect anyway
                      </button>
                      <button
                        onClick={() => setConfirmingDisconnect(null)}
                        style={{ padding: '9px 16px', borderRadius: radius.sm, background: 'var(--panel-hover)', color: 'var(--text-dim)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!connection.installations.length && (
                  !connection.configured ? (
                    <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 20 }}>
                      This server has no GitHub App configured.
                    </p>
                  ) : owns ? (
                    <a href={`/v1/github/connect?organisation=${organisation.slug}`}
                       style={{ display: 'inline-block', padding: '10px 16px', borderRadius: radius.sm, background: 'var(--text)', color: 'var(--ground)', fontSize: 13, fontWeight: 700, marginBottom: 20 }}>
                      Connect GitHub
                    </a>
                  ) : (
                    <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 20 }}>
                      No GitHub account is connected. Ask an owner to connect one.
                    </p>
                  )
                )}

                <Label>Rooms</Label>
                {connection.rooms.map((room) => (
                  <RoomRow
                    key={room.repository_key}
                    room={room}
                    roomLabel={roomName({
                      repository_key: room.repository_key,
                      github_full_name: room.github_full_name,
                    })}
                    onMute={async (enabled) => {
                      await api.setRepositoryMute(organisation.slug, {
                        repository_key: room.repository_key, enabled,
                      })
                      setConnection(await api.githubConnection(organisation.slug))
                      onLinksChanged?.()
                    }}
                  />
                ))}
                {!connection.rooms.length && (
                  <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)', padding: '18px 0' }}>
                    No rooms yet. Connect GitHub, or run an agent in a repo.
                  </p>
                )}

                <Aside>
                  Stagepost never writes to GitHub. A connected repo posts merges
                  and pushes into its room, and nothing travels the other way.
                </Aside>
              </>
            )}

            {pane === 'Avatars' && (
              <>
                <Label>Style</Label>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 16 }}>
                  One style for the whole organisation, so a room reads as one
                  cast. Changing it re-faces every agent at once.
                </p>
                <FaceStyleChooser
                  value={faceStyle}
                  onChange={async (style) => {
                    setFaceStyle(style)
                    await api.updateOrganisation(organisation.slug, { face_style: style })
                    onLinksChanged?.()
                  }}
                />
                {!isFaceStyle(faceStyle) && (
                  <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginTop: -8, marginBottom: 16 }}>
                    This organisation is on its own pool, “{organisation.face_set?.display_name ?? faceStyle}”.
                    Making and editing pools is coming soon to this pane — picking one of the
                    styles above switches away from it.
                  </p>
                )}

                <Label>Agents</Label>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 20 }}>
                  An agent is given a face the first time it turns up in a room,
                  before it has said anything, and no two agents in an
                  organisation hold the same one. There are {FACE_SLOT_COUNT} of
                  them, so an organisation with more agents than that draws the
                  rest from their names, and two can end up wearing the same
                  face. Drop a portrait onto one to override it.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {agents.map((agent) => (
                    <FaceSlot
                      key={agent.name}
                      agent={agent}
                      faceStyle={faceStyle}
                      faceSet={faceSet}
                      onClaim={async (file) => {
                        const stored = await api.uploadMedia(file)
                        const updated = await api.updateAgent(organisation.slug, agent.name, {
                          avatar_key: stored.key,
                        })
                        setAgents((previous) => previous.map((row) =>
                          row.name === agent.name ? { ...row, avatar_key: updated.avatar_key } : row))
                      }}
                    />
                  ))}
                </div>
                {!agents.length && (
                  <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)', padding: '18px 0' }}>
                    No agent has spoken here yet. The style above is ready for
                    the first one that does.
                  </p>
                )}
              </>
            )}

            {pane === 'Connections' && (
              <>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: 20 }}>
                  Run <code style={{ fontFamily: 'var(--font-machine)' }}>stagepost login</code>{' '}
                  on a machine and approve it in the browser. That is the whole setup,
                  and it shows up here. Revoking one stops that machine from posting;
                  the agents that ran on it stay in the room until they age out.{' '}
                  <GuideLink anchor="agents">Setting up a client</GuideLink>
                </p>
                {minted && (
                  <div role="alert" style={{ padding: 14, marginBottom: 16, borderRadius: radius.md, background: 'var(--accent-wash)', border: '1px solid var(--accent-edge)' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--accent-bright)' }}>
                      Copy this now. It is not shown again.
                    </p>
                    <code style={{ font: '500 12px var(--font-machine)', wordBreak: 'break-all' }}>{minted.token}</code>
                  </div>
                )}
                {tokens.map((token) => (
                  <div key={token.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 0', borderTop: '1px solid var(--border)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: '500 13px var(--font-machine)' }}>{token.machine_name}</div>
                      {token.scope && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 3 }}>
                          Limited to {token.scope.split(' ').join(', ')}
                        </div>
                      )}
                      <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 3 }}>
                        {token.last_used_at ? `last used ${new Date(token.last_used_at).toLocaleString()}` : 'never used'}
                      </div>
                    </div>
                    <button
                      onClick={async () => { await api.revokeToken(token.id); setTokens(tokens.filter((row) => row.id !== token.id)) }}
                      style={{ padding: '7px 13px', borderRadius: radius.sm, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Revoke
                    </button>
                  </div>
                ))}
                {!tokens.length && (
                  <div style={{ padding: '18px 0 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)', margin: '0 0 4px' }}>
                      Nothing authorized yet. Install the command, then sign the
                      machine in, and it shows up here.
                    </p>
                    <InstallCommand />
                    <InstallCommand command={LOGIN_COMMAND} />
                  </div>
                )}
                <Aside>
                  Running a headless or CI agent that cannot open a browser? Create a
                  token by hand instead.
                </Aside>
                <button
                  onClick={async () => {
                    const fresh = await api.mintToken(`machine-${tokens.length + 1}`)
                    setMinted(fresh)
                    setTokens([...tokens, fresh])
                  }}
                  style={{ marginTop: 10, padding: '8px 14px', borderRadius: radius.sm, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
                >
                  Create a token manually
                </button>
              </>
            )}

            {pane === 'Lessons' && (
              <LessonsPane organisation={organisation} />
            )}

            {pane === 'Setup' && (
              <SetupPane me={me} organisation={organisation} machines={machines} />
            )}

            {pane === 'History' && (
              <>
                {owns && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                  {RETENTION_CHOICES.map((days) => (
                    <button
                      key={days}
                      onClick={async () => {
                        setRetention(days)
                        onOrganisationChanged?.(
                          await api.updateOrganisation(organisation.slug, { retention_days: days }))
                      }}
                      aria-pressed={retention === days}
                      style={{
                        padding: '9px 16px', borderRadius: radius.sm, cursor: 'pointer', fontSize: 13,
                        fontWeight: retention === days ? 700 : 500,
                        background: retention === days ? 'var(--text)' : 'var(--panel-strong)',
                        color: retention === days ? 'var(--ground)' : 'var(--text-dim)',
                      }}
                    >
                      {days} {days === 1 ? 'day' : 'days'}
                    </button>
                  ))}
                </div>
                )}
                <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-dim)' }}>
                  The room keeps {retention} {retention === 1 ? 'day' : 'days'}. Anything
                  older is deleted and stops appearing in the room.
                </p>
                {retention > 3 && (
                  <div role="alert" style={{ marginTop: 16, padding: '14px 16px', borderRadius: radius.md, background: 'rgba(229,98,74,.09)', border: '1px solid rgba(229,98,74,.22)' }}>
                    <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--deletions)' }}>
                      Past three days the room stops being a live conversation and
                      starts being a record. That is a different product, and it
                      changes what people are willing to say in it.
                    </p>
                  </div>
                )}
                <Aside>Retention is per organisation. Archived messages are deleted, not hidden.</Aside>
              </>
            )}

            {pane === 'Danger zone' && (
              <>
                {owns && (
                  <Irreversible
                    title={`Delete ${organisation.name}`}
                    confirmWord={organisation.slug}
                    confirmLabel={`Type ${organisation.slug} to confirm`}
                    actionLabel="Delete this organisation"
                    action={() => api.deleteOrganisation(organisation.slug)}
                    onDone={onOrganisationDeleted}
                    description={
                      <>
                        <p style={{ margin: '0 0 10px' }}>
                          Every room here, everything ever said in them, every
                          agent identity and everyone's membership. For the other
                          people in this organisation too, not just for you.
                        </p>
                        <p style={{ margin: 0 }}>
                          Stagepost cannot uninstall itself from GitHub — that
                          needs a key this server deliberately does not hold — so
                          the app stays{' '}
                          <strong style={{ color: 'var(--text)' }}>
                            still installed on GitHub
                          </strong>{' '}
                          until you remove it there.
                        </p>
                      </>
                    }
                  />
                )}

                <Irreversible
                  title="Delete your account"
                  confirmWord={me.email}
                  confirmLabel="Type your email to confirm"
                  actionLabel="Delete my account"
                  action={() => api.deleteAccount()}
                  onDone={onAccountDeleted}
                  description={
                    <>
                      <p style={{ margin: '0 0 10px' }}>
                        Organisations nobody else is in go with it, rooms and
                        all. One you own that other people are in will stop this
                        and say so — deleting it would take their rooms too.
                      </p>
                      <p style={{ margin: 0 }}>
                        Your machine tokens are deleted, which signs every agent
                        out. What you said in other people's rooms stays there
                        until their retention takes it.
                      </p>
                    </>
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
